import React, { useState, useEffect } from 'react';
import { Download, Eye, Filter, Receipt, DollarSign, Mail } from 'lucide-react';
import { paymentReceiptAPI } from '../../services/api';
import SearchBar from '../common/SearchBar';
import Pagination from '../common/Pagination';
import { ToastContainer } from '../common/Toast';
import { useToast } from '../../hooks/useToast';

interface PaymentReceipt {
  paymentReceiptId: number;
  membersMembershipId: number;
  receiptNumber: string;
  amountPaid: number;
  discount: number;
  paymentDate: string;
  paymentMethod: string;
  notes: string;
  memberName?: string;
  planName?: string;
  createdAt: string;
}

const Payments: React.FC = () => {
  const [payments, setPayments] = useState<PaymentReceipt[]>([]);
  const [filteredPayments, setFilteredPayments] = useState<PaymentReceipt[]>([]);
  const [paginatedPayments, setPaginatedPayments] = useState<PaymentReceipt[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Toast notifications
  const { toasts, removeToast, success, error, info } = useToast();
  
  // Search and Filter state
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [dateFilter, setDateFilter] = useState<string>('all'); // all, today, week, month
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(25);

  useEffect(() => {
    fetchPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply filters and search
  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payments, searchQuery, dateFilter]);

  // Apply pagination
  useEffect(() => {
    applyPagination();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredPayments, currentPage, itemsPerPage]);

  const fetchPayments = async () => {
    try {
      const data = await paymentReceiptAPI.getAll();
      setPayments(data);
    } catch (err) {
      error('Failed to fetch payments');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...payments];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(payment =>
        payment.receiptNumber?.toLowerCase().includes(query) ||
        payment.memberName?.toLowerCase().includes(query) ||
        payment.planName?.toLowerCase().includes(query) ||
        payment.paymentMethod?.toLowerCase().includes(query)
      );
    }

    // Date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      filtered = filtered.filter(payment => {
        const paymentDate = new Date(payment.paymentDate);
        
        switch (dateFilter) {
          case 'today':
            return paymentDate >= today;
          case 'week':
            const weekAgo = new Date(today);
            weekAgo.setDate(weekAgo.getDate() - 7);
            return paymentDate >= weekAgo;
          case 'month':
            const monthAgo = new Date(today);
            monthAgo.setMonth(monthAgo.getMonth() - 1);
            return paymentDate >= monthAgo;
          default:
            return true;
        }
      });
    }

    // Sort by payment date (newest first)
    filtered.sort((a, b) => new Date(b.paymentDate).getTime() - new Date(a.paymentDate).getTime());

    setFilteredPayments(filtered);
    setCurrentPage(1);
  };

  const applyPagination = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setPaginatedPayments(filteredPayments.slice(startIndex, endIndex));
  };

  const clearFilters = () => {
    setSearchQuery('');
    setDateFilter('all');
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (items: number) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(filteredPayments.length / itemsPerPage);

  const handleDownloadReceipt = async (receiptId: number, receiptNumber: string) => {
    try {
      const { blob } = await paymentReceiptAPI.downloadReceipt(receiptId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Receipt_${receiptNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      success('Receipt downloaded successfully!');
    } catch (err: any) {
      const errorMessage = err?.message || err?.response?.data?.message || 'Failed to download receipt. Please try again or contact support.';
      error(errorMessage);
      console.error('Error downloading receipt:', err);
    }
  };

  const handleViewReceipt = async (receiptId: number) => {
    try {
      // Download receipt (PDF or HTML fallback)
      const { blob } = await paymentReceiptAPI.downloadReceipt(receiptId);
      
      // Create object URL from blob
      const url = window.URL.createObjectURL(blob);
      
      // Open in new tab
      const newWindow = window.open(url, '_blank');
      if (!newWindow) {
        error('Please allow pop-ups to view the receipt');
        window.URL.revokeObjectURL(url);
        return;
      }
      
      // Clean up the object URL after a delay (when the window is closed)
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 100);
      
      // PDF is always returned (HTML is converted automatically)
    } catch (err: any) {
      const errorMessage = err?.message || err?.response?.data?.message || 'Failed to load receipt. Please try again or contact support.';
      error(errorMessage);
      console.error('Error viewing receipt:', err);
    }
  };

  const handleResendReceiptEmail = async (receiptId: number, receiptNumber: string) => {
    try {
      const result = await paymentReceiptAPI.resendEmail(receiptId);
      success(`Receipt ${receiptNumber} sent successfully to member's email!`);
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to send receipt email');
      console.error('Error sending receipt email:', err);
    }
  };

  const handleExportToExcel = async () => {
    try {
      info('Exporting payments to Excel...');
      const blob = await paymentReceiptAPI.exportToExcel();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `PaymentReceipts_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      success('Payments exported to Excel successfully!');
    } catch (err) {
      error('Failed to export payments to Excel');
      console.error('Error exporting to Excel:', err);
    }
  };

  const getTotalAmount = () => {
    return filteredPayments.reduce((sum, payment) => sum + payment.amountPaid, 0);
  };

  if (loading) {
    return <div className="loading">Loading payments...</div>;
  }

  return (
    <div>
      <div className="header">
        <h1 className="header-title">Payment History</h1>
        <button className="btn btn-success" onClick={handleExportToExcel}>
          <Download size={20} style={{ marginRight: '0.5rem' }} />
          Export to Excel
        </button>
      </div>

      {/* Stats Cards */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        <div className="card" style={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white'
        }}>
          <div className="card-body">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.5rem' }}>Total Payments</div>
                <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{filteredPayments.length}</div>
              </div>
              <Receipt size={40} style={{ opacity: 0.8 }} />
            </div>
          </div>
        </div>

        <div className="card" style={{ 
          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          color: 'white'
        }}>
          <div className="card-body">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.5rem' }}>Total Amount</div>
                <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>₹{getTotalAmount().toFixed(2)}</div>
              </div>
              <DollarSign size={40} style={{ opacity: 0.8 }} />
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="card-body">
          <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#374151', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Filter size={20} />
            Search & Filters
          </h3>

          {/* Search Bar */}
          <div style={{ marginBottom: '1rem' }}>
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search by receipt number, member name, plan, or payment method..."
              onClear={() => setSearchQuery('')}
            />
          </div>

          {/* Filter Options */}
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Date Filter */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.875rem', color: '#374151' }}>
                Date Range
              </label>
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                style={{
                  padding: '0.5rem',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  minWidth: '150px'
                }}
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">Last 7 Days</option>
                <option value="month">Last 30 Days</option>
              </select>
            </div>

            {/* Clear Filters */}
            {(searchQuery || dateFilter !== 'all') && (
              <button
                onClick={clearFilters}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#EF4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  marginTop: '1.5rem'
                }}
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Results Summary */}
      <div style={{ 
        marginBottom: '1rem', 
        padding: '1rem',
        backgroundColor: '#F3F4F6',
        borderRadius: '8px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <span style={{ color: '#374151', fontWeight: '500' }}>
          Showing {paginatedPayments.length} of {filteredPayments.length} payments
        </span>
        <span style={{ color: '#6B7280', fontSize: '0.875rem' }}>
          Total: ₹{getTotalAmount().toFixed(2)}
        </span>
      </div>

      {/* Payments Table */}
      <div className="card">
        <div className="card-body" style={{ padding: 0 }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse',
              fontSize: '0.875rem'
            }}>
              <thead style={{ backgroundColor: '#F9FAFB', borderBottom: '2px solid #E5E7EB' }}>
                <tr>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Receipt #</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Member</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Plan</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Amount</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Discount</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Payment Date</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Method</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Notes</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'center', fontWeight: '600', color: '#374151' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedPayments.length === 0 ? (
                  <tr>
                    <td colSpan={9} style={{ padding: '2rem', textAlign: 'center', color: '#6B7280' }}>
                      No payments found
                    </td>
                  </tr>
                ) : (
                  paginatedPayments.map((payment, index) => (
                    <tr 
                      key={payment.paymentReceiptId}
                      style={{ 
                        borderBottom: '1px solid #E5E7EB',
                        backgroundColor: index % 2 === 0 ? 'white' : '#F9FAFB'
                      }}
                    >
                      <td style={{ padding: '0.75rem 1rem', fontWeight: '500', color: '#4F46E5' }}>
                        {payment.receiptNumber}
                      </td>
                      <td style={{ padding: '0.75rem 1rem' }}>
                        {payment.memberName || 'N/A'}
                      </td>
                      <td style={{ padding: '0.75rem 1rem' }}>
                        {payment.planName || 'N/A'}
                      </td>
                      <td style={{ padding: '0.75rem 1rem', fontWeight: '600', color: '#059669' }}>
                        ₹{payment.amountPaid.toFixed(2)}
                      </td>
                      <td style={{ padding: '0.75rem 1rem', fontWeight: '600', color: payment.discount > 0 ? '#DC2626' : '#6B7280' }}>
                        {payment.discount > 0 ? `-₹${payment.discount.toFixed(2)}` : '-'}
                      </td>
                      <td style={{ padding: '0.75rem 1rem' }}>
                        {new Date(payment.paymentDate).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '0.75rem 1rem' }}>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          backgroundColor: '#DBEAFE',
                          color: '#1E40AF',
                          borderRadius: '9999px',
                          fontSize: '0.75rem',
                          fontWeight: '500'
                        }}>
                          {payment.paymentMethod || 'N/A'}
                        </span>
                      </td>
                      <td style={{ padding: '0.75rem 1rem', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {payment.notes || '-'}
                      </td>
                      <td style={{ padding: '0.75rem 1rem' }}>
                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                          <button
                            className="btn btn-primary"
                            onClick={() => handleViewReceipt(payment.paymentReceiptId)}
                            style={{ padding: '0.25rem 0.5rem', fontSize: '0.875rem' }}
                            title="View Receipt"
                          >
                            <Eye size={14} />
                          </button>
                          <button
                            className="btn btn-success"
                            onClick={() => handleDownloadReceipt(payment.paymentReceiptId, payment.receiptNumber)}
                            style={{ padding: '0.25rem 0.5rem', fontSize: '0.875rem' }}
                            title="Download Receipt"
                          >
                            <Download size={14} />
                          </button>
                          <button
                            className="btn btn-info"
                            onClick={() => handleResendReceiptEmail(payment.paymentReceiptId, payment.receiptNumber)}
                            style={{ padding: '0.25rem 0.5rem', fontSize: '0.875rem' }}
                            title="Send Receipt via Email"
                          >
                            <Mail size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {filteredPayments.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={filteredPayments.length}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          )}
        </div>
      </div>

      {/* Toast Container */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default Payments;

